import React, { useMemo } from 'react';
import { Button, Dropdown, Space, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { DownloadOutlined, EyeOutlined, FolderOpenOutlined, MoreOutlined } from '@ant-design/icons';
import {
  partitionRowActionsForPresentation,
  type CompositionRowAction,
} from '../../lib/compositionRowActions';

export type RowActionsProps = {
  availableActions: CompositionRowAction[];
  onAction: (action: CompositionRowAction) => void;
  loading?: boolean;
};

/**
 * Row action shell — presentation only; execution via Action Registry in parent.
 */
const RowActions: React.FC<RowActionsProps> = ({ availableActions, onAction, loading = false }) => {
  const { primary, preview, download, menu } =
    partitionRowActionsForPresentation(availableActions);

  const menuItems: MenuProps['items'] = useMemo(
    () =>
      menu.map((action) => ({
        key: action.kind,
        label: action.label,
        onClick: () => onAction(action),
      })),
    [menu, onAction]
  );

  return (
    <Space size={2} className="cp-composition__row-actions">
      {primary ? (
        <Tooltip title={primary.label}>
          <Button
            type="link"
            size="small"
            icon={<FolderOpenOutlined />}
            onClick={() => onAction(primary)}
            loading={loading}
            disabled={loading}
            aria-label={primary.label}
            tabIndex={-1}
          >
            Привязать
          </Button>
        </Tooltip>
      ) : null}
      <Tooltip title={preview?.label ?? 'Просмотр недоступен'}>
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          disabled={preview == null || loading}
          aria-label={preview?.label ?? 'Просмотр недоступен'}
          onClick={() => {
            if (preview) {
              onAction(preview);
            }
          }}
          tabIndex={-1}
        />
      </Tooltip>
      <Tooltip title={download?.label ?? 'Скачивание недоступно'}>
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          disabled={download == null || loading}
          aria-label={download?.label ?? 'Скачивание недоступно'}
          onClick={() => {
            if (download) {
              onAction(download);
            }
          }}
          tabIndex={-1}
        />
      </Tooltip>
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={menu.length === 0 || loading}>
        <Tooltip
          title={
            menu.length > 0
              ? menu.map((action) => action.label).join(' · ')
              : 'Действия недоступны'
          }
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            disabled={menu.length === 0 || loading}
            loading={loading}
            aria-label="Дополнительные действия"
            tabIndex={-1}
          />
        </Tooltip>
      </Dropdown>
    </Space>
  );
};

export default RowActions;
